import { ExcentOneComponentBaseProps, ExcentOneDisabledComponentProps } from "@excent-one/ui-framework";
import { StackLayout } from "@progress/kendo-react-all";
import {
  Button,
  ButtonHandle,
  ButtonProps,
} from "@progress/kendo-react-buttons";
import {
  Calendar,
  CalendarProps,
  DateRangePicker,
  DateRangePickerChangeEvent,
  DateRangePickerProps,
  MultiViewCalendar,
  MultiViewCalendarProps,
  SelectionRange,
} from "@progress/kendo-react-dateinputs";
import { Popup, PopupProps } from "@progress/kendo-react-popup";
import { cancelIcon, checkIcon, eraserIcon } from "@progress/kendo-svg-icons";
import { isNil } from "lodash";
import { DateTime } from "luxon";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const POPUP_CLASS = "prm-date-range-picker-popup";

interface FooterButtonProps extends Omit<ButtonProps, "onClick" | "disabled"> {
  text?: string;
}

interface DateRangePickerButtonProps
  extends
    ExcentOneDisabledComponentProps,
    Pick<ButtonProps, "onClick">,
    Pick<DateRangePickerProps, "onChange">,
    Pick<CalendarProps, "showOtherMonthDays"> {
  buttonProps?: Omit<ButtonProps, "onClick" | "ref">;
  dateRangeProps?: Omit<
    DateRangePickerProps,
    "onChange" | "show" | "popupSettings.anchor"
  >;
  confirmButtonProps?: FooterButtonProps;
  cancelButtonProps?: FooterButtonProps;
  clearButtonProps?: FooterButtonProps;
  onConfirm?: (range: SelectionRange) => void;
}

const noDateInput = () => null;

const EMPTY_RANGE: SelectionRange = { start: null, end: null };

export const DateRangePickerButton = ({
  children,
  onChange,
  onConfirm,
  buttonProps,
  dateRangeProps,
  disabled: _disabled,
  showOtherMonthDays = false,
  confirmButtonProps = {
    text: "Confirm",
  },
  cancelButtonProps = {
    text: "Cancel",
  },
  clearButtonProps = {
    text: "Clear",
  },
}: PropsWithChildren<DateRangePickerButtonProps>) => {
  const buttonRef = useRef<ButtonHandle>(null);
  const [show, setShow] = useState(false);
  const [selectedRange, setSelectedRange] =
    useState<SelectionRange>(EMPTY_RANGE);
  const toggleDatePicker = useCallback(() => setShow((s) => !s), [setShow]);

  useEffect(() => {
    if (!show) setSelectedRange(EMPTY_RANGE);
  }, [show]);

  const customCalendar = useCallback(
    (props: MultiViewCalendarProps<any>) => (
      <MultiViewCalendar {...props} showOtherMonthDays={false} />
    ),
    [showOtherMonthDays],
  );

  const handleChange = useCallback(
    (event: DateRangePickerChangeEvent) => {
      if (onChange) onChange(event);
      if (event.syntheticEvent.isDefaultPrevented()) return;
      setSelectedRange(event.value);
    },
    [onChange, setSelectedRange],
  );

  const hasSelection =
    !isNil(selectedRange) &&
    !isNil(selectedRange.start) &&
    !isNil(selectedRange.end);

  const handleConfirm = useCallback(() => {
    onConfirm?.(selectedRange);
    setShow(false);
  }, [onConfirm, selectedRange]);

  const handleCancel = useCallback(() => {
    setSelectedRange(EMPTY_RANGE);
    setShow(false);
  }, []);

  const handleClear = useCallback(() => {
    setSelectedRange(EMPTY_RANGE);
  }, []);

  const confirmBtnProps = useMemo<ButtonProps>(() => {
    const { children, text, ...rest } = confirmButtonProps;

    return {
      themeColor: "primary",
      size: "small",
      svgIcon: checkIcon,
      ...rest,
      disabled: !hasSelection || _disabled,
      onClick: handleConfirm,
      children: children || text,
    };
  }, [confirmButtonProps, hasSelection, _disabled, handleConfirm]);

  const cancelBtnProps = useMemo<ButtonProps>(() => {
    const { children, text, ...rest } = cancelButtonProps;

    return {
      themeColor: "base",
      fillMode: "outline",
      svgIcon: cancelIcon,
      size: "small",
      ...rest,
      disabled: !hasSelection || _disabled,
      onClick: handleCancel,
      children: children || text,
    };
  }, [cancelButtonProps, hasSelection, _disabled, handleCancel]);

  const clearBtnProps = useMemo<ButtonProps>(() => {
    const { children, text, ...rest } = clearButtonProps;

    return {
      themeColor: "warning",
      fillMode: "outline",
      svgIcon: eraserIcon,
      size: "small",
      ...rest,
      disabled: !hasSelection || _disabled,
      onClick: handleClear,
      children: children || text,
    };
  }, [clearButtonProps, hasSelection, _disabled, handleClear]);

  // Kept in a ref so `customPopup` keeps a stable identity across renders.
  // DateRangePicker treats the `popup` prop as a component type, so a new
  // function identity on every selection change was causing it to unmount
  // and remount the popup (and its calendar) instead of just re-rendering it.
  const footerBtnPropsRef = useRef({
    confirmBtnProps,
    cancelBtnProps,
    clearBtnProps,
  });
  footerBtnPropsRef.current = {
    confirmBtnProps,
    cancelBtnProps,
    clearBtnProps,
  };

  const customPopup = useCallback(({ children, ...rest }: PopupProps) => {
    const { confirmBtnProps, cancelBtnProps, clearBtnProps } =
      footerBtnPropsRef.current;

    return (
      <Popup {...rest}>
        <StackLayout orientation="vertical">
          {children}
          <StackLayout
            orientation="horizontal"
            align={{ horizontal: "center", vertical: "middle" }}
            gap={8}
          >
            <Button {...confirmBtnProps} />
            <Button {...cancelBtnProps} />
            <Button {...clearBtnProps} />
          </StackLayout>
        </StackLayout>
      </Popup>
    );
  }, []);

  useEffect(() => {
    if (!show) return;

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      const buttonElement = buttonRef.current?.element;
      const popupElement = (target as Element)?.closest?.(`.${POPUP_CLASS}`);

      if (buttonElement?.contains(target) || popupElement) return;

      setShow(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [show]);

  return (
    <>
      <Button {...buttonProps} ref={buttonRef} onClick={toggleDatePicker}>
        {children}
      </Button>
      {buttonRef.current && (
        <DateRangePicker
          adaptive
          adaptiveSubtitle=""
          calendarSettings={{
            views: 1,
          }}
          focusedDate={DateTime.now().toJSDate()}
          {...dateRangeProps}
          value={selectedRange}
          onChange={handleChange}
          show={show}
          startDateInput={noDateInput}
          endDateInput={noDateInput}
          popupSettings={{
            anchorAlign: {
              horizontal: "right",
              vertical: "bottom",
            },
            popupAlign: {
              horizontal: "right",
              vertical: "top",
            },
            ...dateRangeProps?.popupSettings,
            anchor: buttonRef.current?.element,
            popupClass: POPUP_CLASS,
          }}
          calendar={customCalendar}
          popup={customPopup}
        />
      )}
    </>
  );
};
